/**
 * @file Fabrique procedurale de cibles par THEME (registre data-driven) : sapin, palmier, feuillu, cactus, rochers, filons, buisson, monstres, boss.
 *       Produit des prototypes FUSIONNES par couleur, rendus en InstancedMesh (views/InstancedTargets).
 */
import type { BufferGeometry } from 'three';
import type { TargetDef, TargetPalette, TargetThemeId } from '@config/types';
import type { GeometryLibrary } from '../gpu/GeometryLibrary';
import type { ResourceTracker } from '../gpu/ResourceTracker';
import { mixHex } from './MergeBuilder';
import type { PartOptions } from './MergeBuilder';
import { BRICK, DARK, GLASS, houseChimney, houseColors, HOUSE_FOUNDATION, HOUSE_WALL, LEAF, LEAF_DARK, PINK, polarBand, pick, Shape, TAU, TRUNK, WHITE, YELLOW } from './ThemeKit';
import type { TargetProto, ThemeBuilder } from './ThemeKit';
import { HUB_THEME_BUILDERS } from './HubThemes';

export type { ProtoPart, TargetProto, ThemeBuildContext, ThemeBuilder } from './ThemeKit';

type Built = Omit<TargetProto, 'key'>;

/**
 * Lac / etang (empreinte ~6.4 de rayon, collider 6.5) : cuvette a contour irregulier (selon `variant`), eau claire bordee d'ecume,
 * centre plus profond, berge de sable surelevee en bourrelet (vue 3/4 : la rive cache un peu l'eau proche, d'ou un contour net), nenuphars
 * fleuris, roseaux et galets sur la rive. Tout reste bas (< 1.3) pour ne pas masquer les chats.
 */
const buildLake = (p: TargetPalette, variant: number, rand: () => number, geo: GeometryLibrary): Built => {
  const s = new Shape();
  const sand = p.trunk ?? '#d9c08a';
  const wet = mixHex(sand, p.secondary, 0.3);
  const ph = [rand() * TAU, rand() * TAU, rand() * TAU, rand() * TAU] as const;
  const amp = [0.07 + 0.03 * rand(), 0.05 + 0.03 * rand(), 0.03 + 0.02 * rand()] as const;
  /** Rayon du bord de l'eau en fonction de l'angle (~4.6 +- 17 %). */
  const edge = (t: number): number => 4.6 * (1 + amp[0] * Math.cos(2 * t + ph[0]) + amp[1] * Math.cos(3 * t + ph[1]) + amp[2] * Math.cos(5 * t + ph[2]));
  const N = 48;
  const WATER_Y = 0.07;
  const deep = (t: number): number => edge(t) * (0.5 + 0.05 * Math.cos(7 * t + ph[3]));

  // eau : centre profond, eau claire, ecume ondulee au bord
  s.add(polarBand(N, () => [0, WATER_Y + 0.01], (t) => [deep(t), WATER_Y + 0.01]), p.secondary);
  s.add(polarBand(N, (t) => [deep(t) - 0.05, WATER_Y], (t) => [edge(t) - 0.2, WATER_Y]), p.main);
  s.add(polarBand(N, (t) => [edge(t) - 0.4 - 0.1 * Math.cos(13 * t + ph[3]), WATER_Y + 0.012], (t) => [edge(t) - 0.02, WATER_Y + 0.012]), p.accent);
  // berge : sable mouille, bourrelet, pente exterieure
  s.add(polarBand(N, (t) => [edge(t) - 0.02, WATER_Y + 0.005], (t) => [edge(t) + 0.14, 0.16]), wet);
  s.add(polarBand(N, (t) => [edge(t) + 0.14, 0.16], (t) => [edge(t) + 0.38, 0.22]), sand);
  s.add(polarBand(N, (t) => [edge(t) + 0.38, 0.22], (t) => [edge(t) + 0.75, 0.12]), sand);
  s.add(polarBand(N, (t) => [edge(t) + 0.75, 0.12], (t) => [edge(t) + 1.0, 0.0]), mixHex(sand, DARK, 0.1));

  // nenuphars (disques plats) et fleurs
  const pads = 3 + (variant % 2) * 2;
  for (let i = 0; i < pads; i++) {
    const a = rand() * TAU;
    const d = (0.2 + 0.5 * rand()) * edge(a);
    const r = 0.36 + 0.2 * rand();
    const x = Math.cos(a) * d;
    const z = Math.sin(a) * d;
    s.add(geo.cylinder(1, 1, 1, 12), i % 2 === 0 ? LEAF : LEAF_DARK, { x, y: WATER_Y + 0.07, z, sx: r, sz: r, sy: 0.07 });
    if (i % 2 === 0) {
      s.add(geo.sphere(0.13, 8, 6), i % 4 === 0 ? PINK : WHITE, { x, y: WATER_Y + 0.2, z, sy: 0.8 });
      s.add(geo.sphere(0.05, 6, 5), YELLOW, { x, y: WATER_Y + 0.29, z });
    }
  }
  // roseaux (touffes de 3-4 tiges, quelques epis bruns) sur la berge
  const clumps = 3 + (variant % 2);
  for (let c = 0; c < clumps; c++) {
    const a = ((c + 0.2 + 0.6 * rand()) / clumps) * TAU;
    const d = edge(a) + 0.5;
    const stems = 2 + Math.floor(rand() * 2);
    for (let k = 0; k < stems; k++) {
      const h = 0.6 + 0.5 * rand();
      const sa = rand() * TAU;
      const x = Math.cos(a) * d + Math.cos(sa) * 0.12;
      const z = Math.sin(a) * d + Math.sin(sa) * 0.12;
      const tiltX = (rand() - 0.5) * 0.3;
      const tiltZ = (rand() - 0.5) * 0.3;
      s.add(geo.cylinder(0.05, 0.07, h, 5), k % 2 === 0 ? LEAF_DARK : LEAF, { x, y: 0.16 + h / 2, z, rx: tiltX, rz: tiltZ });
      if (k === 0) s.add(geo.capsule(0.06, 0.14, 3, 6), mixHex(p.trunk ?? sand, DARK, 0.45), { x: x - tiltZ * h * 0.5, y: 0.16 + h + 0.04, z: z + tiltX * h * 0.5 });
    }
  }
  // galets sur la rive
  const stones = 4 + (variant % 2) * 2;
  for (let i = 0; i < stones; i++) {
    const a = rand() * TAU;
    const d = edge(a) + 0.4 + 0.5 * rand();
    const r = 0.15 + 0.1 * rand();
    s.add(geo.ico(1, 0), i % 2 === 0 ? mixHex(sand, DARK, 0.3) : p.accent, { x: Math.cos(a) * d, y: 0.15 + r * 0.3, z: Math.sin(a) * d, sx: r * 1.3, sy: r * 0.8, sz: r, ry: rand() * 3 });
  }
  return s.finish();
};

/**
 * Maisonnette kawaii (empreinte ~1.5 de rayon, collider 1.5) : soubassement, murs `main`, toit `secondary`, cheminee fumante, porte arrondie
 * avec cadre et marche, fenetre a cadre `accent` et jardiniere. Face avant = +Z. Variantes : 0 toit pyramide, 1 toit a pignon (couleur
 * bleutee), 2 tourelle ronde a toit conique (couleur verte).
 */
const buildHouse = (p: TargetPalette, variant: number, rand: () => number, geo: GeometryLibrary): Built => {
  const s = new Shape();
  const wood = p.trunk ?? '#a9794a';
  const v = variant % 3;
  const { wall, roof, base } = houseColors(p, variant);
  const W = 2.0;
  const D = 1.8;
  const H = HOUSE_WALL;
  const FOUND = HOUSE_FOUNDATION;
  const top = FOUND + H;
  const round = v === 2;
  const front = D / 2;

  // soubassement + murs
  if (round) {
    s.add(geo.cylinder(1.12, 1.12, FOUND, 16), base, { y: FOUND / 2 });
    s.add(geo.cylinder(1.0, 1.04, H, 16), wall, { y: FOUND + H / 2 });
  } else {
    s.add(geo.box(W + 0.12, FOUND, D + 0.12), base, { y: FOUND / 2 });
    s.add(geo.box(W, H, D), wall, { y: FOUND + H / 2 });
  }

  // toit
  const chimney = houseChimney(v);
  if (v === 0) {
    // toit a deux etages (pagode mignonne) : avant-toit large et bas, puis pyramide plus claire et plus raide
    s.add(geo.cone(1.5, 0.5, 4), roof, { y: top + 0.25, ry: Math.PI / 4 });
    s.add(geo.cone(1.0, 0.62, 4), mixHex(roof, WHITE, 0.22), { y: top + 0.7, ry: Math.PI / 4 });
    s.add(geo.sphere(0.1, 8, 6), p.accent, { y: top + 1.03 });
  } else if (v === 1) {
    // prisme triangulaire (arete le long de Z, pignon face a la camera)
    const sx = (W + 0.24) / 1.732;
    const sz = 0.58;
    const len = D + 0.2;
    s.add(geo.cylinder(1, 1, 1, 3), roof, { y: top + 0.5 * sz - 0.02, rx: -Math.PI / 2, sx, sy: len, sz });
    // pignons (murs) : prisme legerement plus etroit et plus long que le toit, il ne depasse qu'aux deux extremites
    s.add(geo.cylinder(1, 1, 1, 3), wall, { y: top + 0.5 * sz - 0.02, rx: -Math.PI / 2, sx: sx * 0.9, sy: len + 0.05, sz: sz * 0.9 });
    s.add(geo.cylinder(0.13, 0.13, 0.04, 12), p.accent, { y: top + 0.22, z: len / 2 + 0.03, rx: Math.PI / 2 });
  } else {
    s.add(geo.cone(1.28, 1.05, 14), roof, { y: top + 0.52 });
    s.add(geo.sphere(0.11, 8, 6), p.accent, { y: top + 1.08 });
    s.add(geo.cylinder(1.1, 1.1, 0.07, 16), mixHex(roof, DARK, 0.15), { y: top + 0.02 });
  }

  // cheminee (la fumee est animee a part : fx/ChimneySmoke)
  s.add(geo.box(0.32, chimney.h, 0.32), BRICK, { x: chimney.x, y: chimney.y, z: chimney.z });
  s.add(geo.box(0.42, 0.08, 0.42), mixHex(BRICK, DARK, 0.25), { x: chimney.x, y: chimney.y + chimney.h / 2, z: chimney.z });

  // pose d'un detail sur la facade (a l'abscisse locale `ax`) : plane, ou tangente au cylindre de la tourelle
  const onFacade = (ax: number, lx: number, ly: number, lz: number, g: BufferGeometry, color: string, o: PartOptions = {}): void => {
    const fz = round ? Math.sqrt(1 - ax * ax) : front;
    const yaw = round ? Math.atan2(ax, fz) : 0;
    const c = Math.cos(yaw);
    const sn = Math.sin(yaw);
    s.add(g, color, { x: ax + lx * c + lz * sn, y: ly, z: fz - lx * sn + lz * c, ry: yaw, ...o });
  };

  // porte arrondie + cadre + poignee + marche
  const dx = round ? 0.32 : 0.38;
  const door = (lx: number, ly: number, lz: number, g: BufferGeometry, color: string, o: PartOptions = {}): void => onFacade(dx, lx, ly, lz, g, color, o);
  door(0, FOUND + 0.4, 0.02, geo.box(0.7, 0.8, 0.06), p.accent);
  door(0, FOUND + 0.8, 0.02, geo.cylinder(0.35, 0.35, 0.06, 14), p.accent, { rx: Math.PI / 2 });
  door(0, FOUND + 0.38, 0.06, geo.box(0.54, 0.76, 0.06), wood);
  door(0, FOUND + 0.76, 0.06, geo.cylinder(0.27, 0.27, 0.06, 14), wood, { rx: Math.PI / 2 });
  door(0.16, FOUND + 0.4, 0.12, geo.sphere(0.05, 6, 5), p.accent);
  door(0, 0.05, 0.42, geo.box(0.9, 0.1, 0.42), mixHex(base, WHITE, 0.25));

  // fenetre de facade + jardiniere fleurie
  const wx = -0.55;
  const win = (lx: number, ly: number, lz: number, g: BufferGeometry, color: string, o: PartOptions = {}): void => onFacade(wx, lx, ly, lz, g, color, o);
  win(0, FOUND + 0.68, 0.02, geo.box(0.56, 0.56, 0.06), p.accent);
  win(0, FOUND + 0.68, 0.06, geo.box(0.42, 0.42, 0.05), GLASS);
  win(0, FOUND + 0.68, 0.09, geo.box(0.05, 0.42, 0.04), p.accent);
  win(0, FOUND + 0.68, 0.09, geo.box(0.42, 0.05, 0.04), p.accent);
  win(0, FOUND + 0.36, 0.1, geo.box(0.66, 0.1, 0.14), wood);
  [-0.22, 0, 0.22].forEach((fx, i) => win(fx, FOUND + 0.46, 0.1, geo.sphere(0.08, 6, 5), i === 1 ? YELLOW : PINK));

  // fenetre laterale (cote +X) pour les murs plats
  if (!round) {
    s.add(geo.box(0.06, 0.4, 0.4), p.accent, { x: W / 2 + 0.01, y: FOUND + 0.6, z: 0.1 });
    s.add(geo.box(0.07, 0.28, 0.28), GLASS, { x: W / 2 + 0.03, y: FOUND + 0.6, z: 0.1 });
  }
  return s.finish();
};

/**
 * Fontaine a deux etages (empreinte ~1.6 de rayon, collider 1.6) : grande vasque de pierre `main` a margelle, eau `secondary`, pied central,
 * seconde vasque et jet d'eau `accent` retombant en arcs de gouttes.
 */
const buildFountain = (p: TargetPalette, geo: GeometryLibrary): Built => {
  const s = new Shape();
  const light = mixHex(p.main, WHITE, 0.45);
  const shade = mixHex(p.main, DARK, 0.12);
  const water = mixHex(p.secondary, '#3aa0e8', 0.4);
  // vasque basse : socle, cuve evasee (creuse : l'eau affleure sous la margelle)
  s.add(geo.cylinder(1.35, 1.5, 0.16, 20), shade, { y: 0.08 });
  s.add(geo.cylinder(1.58, 1.34, 0.36, 20), p.main, { y: 0.32 });
  s.add(geo.torus(1.55, 0.11), light, { y: 0.52, rx: Math.PI / 2 });
  s.add(geo.cylinder(1.47, 1.47, 0.04, 20), water, { y: 0.54 });
  // pied central + seconde vasque
  s.add(geo.cylinder(0.2, 0.32, 0.7, 12), p.main, { y: 0.85 });
  s.add(geo.torus(0.3, 0.07), light, { y: 0.7, rx: Math.PI / 2 });
  s.add(geo.cylinder(0.82, 0.38, 0.28, 18), p.main, { y: 1.26 });
  s.add(geo.torus(0.8, 0.08), light, { y: 1.42, rx: Math.PI / 2 });
  s.add(geo.cylinder(0.74, 0.74, 0.04, 18), water, { y: 1.43 });
  // jet central : colonne d'eau, boule d'ecume, quelques gouttes en arc qui retombent
  s.add(geo.cylinder(0.07, 0.11, 0.5, 8), p.accent, { y: 1.66 });
  s.add(geo.sphere(0.19, 10, 8), p.accent, { y: 1.96 });
  const arms = 5;
  for (let i = 0; i < arms; i++) {
    const a = (i / arms) * TAU + 0.3;
    s.add(geo.sphere(0.1, 6, 5), p.accent, { x: Math.cos(a) * 0.34, y: 1.9, z: Math.sin(a) * 0.34 });
    s.add(geo.sphere(0.09, 6, 5), p.accent, { x: Math.cos(a) * 0.6, y: 1.66, z: Math.sin(a) * 0.6, sy: 1.3 });
  }
  // gouttes qui tombent de la vasque haute dans la basse
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * TAU + 0.9;
    s.add(geo.sphere(0.09, 6, 5), p.accent, { x: Math.cos(a) * 1.0, y: 0.98, z: Math.sin(a) * 1.0, sy: 1.5 });
  }
  // petit bouton de pierre au sommet
  s.add(geo.sphere(0.1, 8, 6), light, { y: 2.2 });
  return s.finish();
};

/**
 * Constructeurs de formes (style kawaii, formes rondes, 2-4 couleurs, echelle 1 ~ 2.2 de haut pour un arbre, monstres ~1.1).
 * Chaque constructeur fusionne ses primitives PAR COULEUR (une ProtoPart par couleur) ; les geometries resultantes sont
 * trackees dans la portee 'global' par TargetFactory.proto (prototypes reutilises entre biomes). Repere : +Y haut, +Z = face
 * des monstres (yeux), origine au sol.
 */
export const THEME_BUILDERS: Record<TargetThemeId, ThemeBuilder> = {
  fir: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    s.add(geo.cylinder(0.11, 0.16, 0.5, 8), p.trunk ?? TRUNK, { y: 0.25 });
    const levels = 3 + (variant % 2);
    const spin = rand() * Math.PI;
    let y = 0.36;
    let top = y;
    for (let i = 0; i < levels; i++) {
      const t = i / (levels - 1);
      const r = (0.84 - 0.52 * t) * (0.94 + 0.12 * rand());
      const h = 0.84 - 0.14 * t;
      s.add(geo.cone(r, h, 9), i % 2 === 0 ? p.main : p.secondary, { y: y + h / 2, ry: spin + i * 0.6, x: (rand() - 0.5) * 0.06, z: (rand() - 0.5) * 0.06 });
      top = y + h;
      y += h * (levels === 3 ? 0.6 : 0.5);
    }
    s.add(geo.sphere(0.1, 8, 6), p.accent, { y: top });
    // petites boules decoratives (neige / guirlande) sur le cone du bas
    for (let i = 0; i < 3; i++) {
      const a = rand() * Math.PI * 2;
      s.add(geo.sphere(0.07, 6, 5), p.accent, { x: Math.sin(a) * 0.5, y: 0.55 + rand() * 0.1, z: Math.cos(a) * 0.5 });
    }
    return s.finish();
  },

  palm: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    const trunk = p.trunk ?? TRUNK;
    const dir = variant % 2 === 0 ? 1 : -1;
    const bend = 0.1 + 0.05 * rand();
    const segs = 5;
    const segLen = 0.36;
    let px = 0;
    let py = 0;
    for (let i = 0; i < segs; i++) {
      const a = dir * i * bend;
      s.add(geo.cylinder(0.09 - i * 0.005, 0.12 - i * 0.006, segLen + 0.04, 8), i % 2 === 0 ? trunk : mixHex(trunk, DARK, 0.18), {
        x: px + Math.sin(a) * (segLen / 2),
        y: py + Math.cos(a) * (segLen / 2),
        rz: -a,
      });
      px += Math.sin(a) * segLen;
      py += Math.cos(a) * segLen;
    }
    const leaves = 5 + Math.floor(rand() * 3);
    for (let k = 0; k < leaves; k++) {
      const ang = (k / leaves) * Math.PI * 2 + rand() * 0.3;
      const len = 0.46 + rand() * 0.1;
      s.add(geo.sphere(1, 10, 6), k % 2 === 0 ? p.main : p.secondary, {
        x: px + Math.sin(ang) * len * 0.8,
        y: py + 0.02 - Math.abs(Math.sin(k * 1.7)) * 0.08,
        z: Math.cos(ang) * len * 0.8,
        ry: ang,
        rx: 0.45 + rand() * 0.2,
        order: 'YXZ',
        sx: 0.19,
        sy: 0.055,
        sz: len,
      });
    }
    s.add(geo.sphere(0.15, 8, 6), p.accent, { x: px, y: py + 0.06 });
    const nuts = 2 + (variant % 2);
    for (let i = 0; i < nuts; i++) {
      const a = (i / nuts) * Math.PI * 2 + 0.6;
      s.add(geo.sphere(0.1, 8, 6), mixHex(trunk, DARK, 0.25), { x: px + Math.sin(a) * 0.14, y: py - 0.1, z: Math.cos(a) * 0.14 });
    }
    return s.finish();
  },

  oak: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    s.add(geo.cylinder(0.13, 0.2, 0.72, 9), p.trunk ?? TRUNK, { y: 0.36 });
    const n = 3 + (variant % 3);
    const cy = 1.18;
    const blobs: Array<{ x: number; y: number; z: number; r: number }> = [{ x: 0, y: cy + 0.12, z: 0, r: 0.64 }];
    for (let i = 1; i < n; i++) {
      const a = (i / (n - 1)) * Math.PI * 2 + rand();
      const d = 0.36 + 0.1 * rand();
      blobs.push({ x: Math.sin(a) * d, y: cy - 0.08 + (rand() - 0.3) * 0.22, z: Math.cos(a) * d, r: 0.42 + 0.14 * rand() });
    }
    blobs.forEach((b, i) => s.add(geo.ico(1, 1), i % 2 === 0 ? p.main : p.secondary, { x: b.x, y: b.y, z: b.z, s: b.r, ry: rand() * 3 }));
    // fruits / fleurs
    for (let i = 0; i < 4; i++) {
      const b = pick(blobs, rand);
      const th = rand() * Math.PI * 2;
      const ph = 0.3 + rand() * 1.0;
      s.add(geo.sphere(0.08, 6, 5), p.accent, {
        x: b.x + Math.sin(th) * Math.sin(ph) * b.r * 0.98,
        y: b.y + Math.cos(ph) * b.r * 0.98,
        z: b.z + Math.cos(th) * Math.sin(ph) * b.r * 0.98,
      });
    }
    if (p.glow) {
      for (let i = 0; i < 6; i++) {
        const b = pick(blobs, rand);
        const th = rand() * Math.PI * 2;
        const ph = 0.2 + rand() * 1.3;
        s.add(
          geo.sphere(0.07, 6, 5),
          p.glow,
          {
            x: b.x + Math.sin(th) * Math.sin(ph) * b.r * 1.02,
            y: b.y + Math.cos(ph) * b.r * 1.02,
            z: b.z + Math.cos(th) * Math.sin(ph) * b.r * 1.02,
          },
          p.glow,
        );
      }
    }
    return s.finish();
  },

  cactus: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    const lenMul = 0.85 + 0.3 * rand();
    const bodyLen = 0.8 * lenMul;
    const half = bodyLen / 2 + 0.27;
    s.add(geo.capsule(0.27, bodyLen, 6, 12), p.main, { y: half });
    const arms = variant % 3 === 0 ? 1 : 2;
    for (let i = 0; i < arms; i++) {
      const side = arms === 1 ? (rand() < 0.5 ? -1 : 1) : i === 0 ? -1 : 1;
      const ay = 0.45 + rand() * 0.22;
      s.add(geo.capsule(0.13, 0.2, 5, 10), p.secondary, { x: side * 0.4, y: ay, rz: Math.PI / 2 });
      s.add(geo.capsule(0.13, 0.28 + rand() * 0.18, 5, 10), p.secondary, { x: side * 0.6, y: ay + 0.26 });
    }
    const topY = half * 2 - 0.02;
    s.add(geo.sphere(0.11, 8, 6), p.accent, { y: topY + 0.06 });
    s.add(geo.sphere(0.06, 6, 5), mixHex(p.accent, WHITE, 0.4), { y: topY + 0.15 });
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      s.add(geo.cone(0.03, 0.12, 4), p.accent, { x: Math.sin(a) * 0.27, y: 0.35 + (i % 3) * 0.25, z: Math.cos(a) * 0.27, rz: -Math.sin(a) * 1.2, rx: Math.cos(a) * 1.2 });
    }
    return s.finish();
  },

  rockSmall: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    const n = 2 + (variant % 2);
    s.add(geo.ico(1, 0), p.main, { y: 0.22, sx: 0.55, sy: 0.4, sz: 0.48, ry: rand() * 3, rz: (rand() - 0.5) * 0.3 });
    for (let i = 1; i < n; i++) {
      const a = rand() * Math.PI * 2;
      s.add(geo.ico(1, 0), i % 2 === 0 ? p.main : p.secondary, { x: Math.sin(a) * 0.42, y: 0.13, z: Math.cos(a) * 0.42, sx: 0.28, sy: 0.22, sz: 0.26, ry: rand() * 3 });
    }
    s.add(geo.ico(1, 0), p.accent, { x: 0.06, y: 0.5, z: 0.04, sx: 0.2, sy: 0.07, sz: 0.17, ry: rand() * 3 });
    return s.finish();
  },

  lake: ({ palette: p, variant, rand, geo }) => buildLake(p, variant, rand, geo),
  house: ({ palette: p, variant, rand, geo }) => buildHouse(p, variant, rand, geo),
  fountain: ({ palette: p, geo }) => buildFountain(p, geo),

  ...HUB_THEME_BUILDERS,

  rockBig: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    s.add(geo.ico(1, 0), p.main, { y: 0.5, sx: 0.8, sy: 0.62, sz: 0.74, ry: rand() * 3, rz: (rand() - 0.5) * 0.2 });
    const n = 2 + (variant % 3);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + rand();
      const k = 0.32 + 0.14 * rand();
      s.add(geo.ico(1, 0), i % 2 === 0 ? p.secondary : p.main, { x: Math.sin(a) * 0.72, y: k * 0.8, z: Math.cos(a) * 0.72, s: k, sy: k * 0.8, ry: rand() * 3 });
    }
    // mousse / neige sur le dessus
    s.add(geo.ico(1, 0), p.accent, { x: 0.05, y: 0.98, z: 0, sx: 0.5, sy: 0.17, sz: 0.46, ry: rand() * 3 });
    return s.finish();
  },

  oreVein: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    s.add(geo.ico(1, 0), p.secondary, { y: 0.26, sx: 0.7, sy: 0.42, sz: 0.62, ry: rand() * 3 });
    const n = 4 + (variant % 3);
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2 + rand() * 0.5;
      const d = k === 0 ? 0.05 : 0.18 + 0.3 * rand();
      const h = (k === 0 ? 0.95 : 0.5 + 0.35 * rand());
      const r = 0.13 + 0.08 * rand();
      const tilt = k === 0 ? 0 : 0.2 + 0.25 * rand();
      s.add(
        geo.cone(r, h, 5),
        p.main,
        { x: Math.sin(a) * d, y: 0.3 + h / 2 - 0.04, z: Math.cos(a) * d, rx: Math.cos(a) * tilt, rz: -Math.sin(a) * tilt, ry: rand() * 3 },
        p.glow,
      );
    }
    for (let k = 0; k < 3; k++) {
      const a = rand() * Math.PI * 2;
      s.add(geo.cone(0.07, 0.28, 4), p.accent, { x: Math.sin(a) * 0.5, y: 0.34, z: Math.cos(a) * 0.5, rx: Math.cos(a) * 0.5, rz: -Math.sin(a) * 0.5 });
    }
    return s.finish();
  },

  bush: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    const sw = variant % 2 === 0 ? 1 : -1;
    s.add(geo.sphere(1, 12, 8), p.main, { x: -0.26 * sw, y: 0.28, z: 0, s: 0.38, sy: 0.32 });
    s.add(geo.sphere(1, 12, 8), p.secondary, { x: 0.27 * sw, y: 0.25, z: 0.05, s: 0.34, sy: 0.3 });
    s.add(geo.sphere(1, 12, 8), p.main, { x: 0, y: 0.32, z: -0.2, s: 0.3, sy: 0.3 });
    for (let i = 0; i < 3; i++) {
      const a = rand() * Math.PI * 2;
      s.add(geo.sphere(0.06, 6, 5), p.accent, { x: Math.sin(a) * 0.35, y: 0.45 + rand() * 0.1, z: Math.cos(a) * 0.3 });
    }
    return s.finish();
  },

  blob: ({ palette: p, geo }) => {
    const s = new Shape();
    s.add(geo.sphere(1, 16, 12), p.main, { y: 0.5, s: 0.6, sy: 0.5 });
    s.add(geo.sphere(1, 12, 8), p.secondary, { y: 0.07, sx: 0.66, sy: 0.12, sz: 0.66 });
    s.add(geo.sphere(0.09, 8, 6), DARK, { x: -0.2, y: 0.6, z: 0.5 });
    s.add(geo.sphere(0.09, 8, 6), DARK, { x: 0.2, y: 0.6, z: 0.5 });
    s.add(geo.sphere(0.03, 6, 4), WHITE, { x: -0.17, y: 0.65, z: 0.57 });
    s.add(geo.sphere(0.03, 6, 4), WHITE, { x: 0.23, y: 0.65, z: 0.57 });
    s.add(geo.sphere(0.08, 6, 5), p.accent, { x: -0.36, y: 0.48, z: 0.44, sz: 0.5 });
    s.add(geo.sphere(0.08, 6, 5), p.accent, { x: 0.36, y: 0.48, z: 0.44, sz: 0.5 });
    s.add(geo.sphere(0.1, 8, 6), DARK, { y: 0.45, z: 0.56, sx: 0.9, sy: 0.4, sz: 0.4 });
    s.add(geo.sphere(1, 8, 6), p.accent, { x: 0.05, y: 0.95, z: 0, sx: 0.12, sy: 0.07, sz: 0.2, rz: 0.5 });
    return s.finish();
  },

  runner: ({ palette: p, geo }) => {
    const s = new Shape();
    s.add(geo.sphere(1, 14, 10), p.main, { y: 0.42, s: 0.42, sy: 0.38 });
    s.add(geo.cone(0.12, 0.3, 8), p.secondary, { x: -0.2, y: 0.82, z: 0.02, rz: 0.35 });
    s.add(geo.cone(0.12, 0.3, 8), p.secondary, { x: 0.2, y: 0.82, z: 0.02, rz: -0.35 });
    s.add(geo.sphere(0.07, 8, 6), p.accent, { x: -0.2, y: 0.8, z: 0.06, sz: 0.5 });
    s.add(geo.sphere(0.07, 8, 6), p.accent, { x: 0.2, y: 0.8, z: 0.06, sz: 0.5 });
    s.add(geo.sphere(0.08, 8, 6), DARK, { x: -0.15, y: 0.5, z: 0.35 });
    s.add(geo.sphere(0.08, 8, 6), DARK, { x: 0.15, y: 0.5, z: 0.35 });
    s.add(geo.sphere(0.03, 6, 4), WHITE, { x: -0.13, y: 0.54, z: 0.41 });
    s.add(geo.sphere(0.03, 6, 4), WHITE, { x: 0.17, y: 0.54, z: 0.41 });
    s.add(geo.sphere(0.11, 8, 6), p.secondary, { x: -0.16, y: 0.08, z: 0.14, sz: 1.3 });
    s.add(geo.sphere(0.11, 8, 6), p.secondary, { x: 0.16, y: 0.08, z: 0.14, sz: 1.3 });
    s.add(geo.cone(0.1, 0.34, 7), p.accent, { y: 0.42, z: -0.5, rx: -Math.PI / 2 });
    return s.finish();
  },

  tank: ({ palette: p, geo }) => {
    const s = new Shape();
    s.add(geo.sphere(1, 16, 12), p.main, { y: 0.72, sx: 0.82, sy: 0.66, sz: 0.74 });
    s.add(geo.sphere(1, 12, 8), p.accent, { y: 0.55, z: 0.4, sx: 0.5, sy: 0.32, sz: 0.34 });
    s.add(geo.cone(0.14, 0.5, 8), p.secondary, { x: -0.45, y: 1.3, z: 0.05, rz: 0.5 });
    s.add(geo.cone(0.14, 0.5, 8), p.secondary, { x: 0.45, y: 1.3, z: 0.05, rz: -0.5 });
    for (const [fx, fz] of [[-0.42, 0.34], [0.42, 0.34], [-0.42, -0.34], [0.42, -0.34]] as const) {
      s.add(geo.sphere(0.22, 8, 6), p.secondary, { x: fx, y: 0.2, z: fz, sy: 0.8 });
    }
    s.add(geo.sphere(0.1, 8, 6), DARK, { x: -0.27, y: 0.92, z: 0.6 });
    s.add(geo.sphere(0.1, 8, 6), DARK, { x: 0.27, y: 0.92, z: 0.6 });
    s.add(geo.box(0.28, 0.07, 0.07), DARK, { x: -0.27, y: 1.08, z: 0.62, rz: -0.35 });
    s.add(geo.box(0.28, 0.07, 0.07), DARK, { x: 0.27, y: 1.08, z: 0.62, rz: 0.35 });
    s.add(geo.cone(0.07, 0.24, 6), WHITE, { x: -0.2, y: 0.55, z: 0.7, rx: Math.PI * 0.75 });
    s.add(geo.cone(0.07, 0.24, 6), WHITE, { x: 0.2, y: 0.55, z: 0.7, rx: Math.PI * 0.75 });
    return s.finish();
  },

  bomber: ({ palette: p, geo }) => {
    const s = new Shape();
    s.add(geo.sphere(1, 16, 12), p.main, { y: 0.55, s: 0.55 });
    s.add(geo.torus(0.56, 0.06), p.secondary, { y: 0.55, rx: Math.PI / 2 });
    s.add(geo.cylinder(0.05, 0.05, 0.3, 6), DARK, { x: 0.04, y: 1.15, rz: -0.25 });
    s.add(geo.sphere(0.11, 8, 6), p.accent, { x: 0.1, y: 1.34 }, p.accent);
    s.add(geo.sphere(0.08, 8, 6), DARK, { x: -0.17, y: 0.64, z: 0.46 });
    s.add(geo.sphere(0.08, 8, 6), DARK, { x: 0.17, y: 0.64, z: 0.46 });
    s.add(geo.sphere(0.03, 6, 4), WHITE, { x: -0.15, y: 0.68, z: 0.52 });
    s.add(geo.sphere(0.03, 6, 4), WHITE, { x: 0.19, y: 0.68, z: 0.52 });
    s.add(geo.sphere(0.09, 8, 6), DARK, { y: 0.44, z: 0.52, sx: 1.1, sy: 0.5, sz: 0.5 });
    s.add(geo.sphere(0.12, 8, 6), p.secondary, { x: -0.18, y: 0.1, z: 0.12, sz: 1.3 });
    s.add(geo.sphere(0.12, 8, 6), p.secondary, { x: 0.18, y: 0.1, z: 0.12, sz: 1.3 });
    return s.finish();
  },

  golem: ({ palette: p, variant, rand, geo }) => {
    const s = new Shape();
    const k = 0.78;
    const ico = (color: string, o: PartOptions): void => {
      s.add(geo.ico(1, 0), color, { ...o, ry: rand() * 3 });
    };
    for (const sx of [-1, 1]) {
      ico(p.secondary, { x: sx * 0.28 * k, y: 0.25 * k, sx: 0.24 * k, sy: 0.3 * k, sz: 0.26 * k });
      ico(p.main, { x: sx * 0.78 * k, y: 1.3 * k, s: 0.34 * k, sy: 0.28 * k });
      s.add(geo.box(0.3 * k, 0.74 * k, 0.3 * k), p.main, { x: sx * 0.84 * k, y: 0.82 * k, rz: sx * 0.08 });
      ico(p.secondary, { x: sx * 0.88 * k, y: 0.4 * k, s: 0.27 * k });
      ico(p.accent, { x: sx * 0.76 * k, y: 1.52 * k, sx: 0.22 * k, sy: 0.07 * k, sz: 0.2 * k });
    }
    ico(p.main, { y: 0.98 * k, sx: 0.72 * k, sy: 0.62 * k, sz: 0.5 * k });
    ico(p.secondary, { y: 0.66 * k, sx: 0.5 * k, sy: 0.3 * k, sz: 0.42 * k });
    ico(p.secondary, { y: 1.78 * k, z: 0.06 * k, sx: 0.34 * k, sy: 0.3 * k, sz: 0.32 * k });
    ico(p.accent, { x: -0.2 * k, y: 1.48 * k, z: -0.1 * k, sx: 0.28 * k, sy: 0.08 * k, sz: 0.24 * k });
    const eye = '#ffe27a';
    s.add(geo.sphere(0.07 * k, 8, 6), eye, { x: -0.14 * k, y: 1.8 * k, z: 0.34 * k }, eye);
    s.add(geo.sphere(0.07 * k, 8, 6), eye, { x: 0.14 * k, y: 1.8 * k, z: 0.34 * k }, eye);
    s.add(geo.box(0.3 * k, 0.07 * k, 0.07 * k), DARK, { y: 1.62 * k, z: 0.36 * k });
    if (variant % 2 === 1) ico(p.accent, { x: 0.22 * k, y: 1.52 * k, z: 0.1 * k, s: 0.12 * k });
    return s.finish();
  },

  dragon: ({ palette: p, geo }) => {
    const s = new Shape();
    s.add(geo.capsule(0.32, 0.5, 6, 12), p.main, { y: 0.6, rx: Math.PI / 2 });
    s.add(geo.sphere(1, 12, 8), p.accent, { y: 0.44, z: 0.08, sx: 0.28, sy: 0.2, sz: 0.5 });
    s.add(geo.sphere(0.27, 12, 8), p.main, { y: 0.88, z: 0.6 });
    s.add(geo.sphere(1, 10, 8), p.main, { y: 0.8, z: 0.84, sx: 0.16, sy: 0.12, sz: 0.2 });
    s.add(geo.sphere(0.07, 8, 6), DARK, { x: -0.15, y: 0.96, z: 0.74 });
    s.add(geo.sphere(0.07, 8, 6), DARK, { x: 0.15, y: 0.96, z: 0.74 });
    s.add(geo.sphere(0.025, 6, 4), WHITE, { x: -0.13, y: 1.0, z: 0.79 });
    s.add(geo.sphere(0.025, 6, 4), WHITE, { x: 0.17, y: 1.0, z: 0.79 });
    for (const sx of [-1, 1]) {
      s.add(geo.cone(0.07, 0.28, 6), p.secondary, { x: sx * 0.13, y: 1.18, z: 0.5, rx: -0.5, rz: -sx * 0.25, order: 'YXZ' });
      s.add(geo.sphere(1, 12, 6), p.secondary, { x: sx * 0.62, y: 0.92, z: -0.05, sx: 0.5, sy: 0.04, sz: 0.34, rz: sx * 0.45 });
      s.add(geo.sphere(1, 10, 6), p.accent, { x: sx * 0.86, y: 1.08, z: -0.08, sx: 0.2, sy: 0.035, sz: 0.2, rz: sx * 0.45 });
      s.add(geo.sphere(0.12, 8, 6), p.secondary, { x: sx * 0.25, y: 0.12, z: 0.3, sz: 1.3 });
      s.add(geo.sphere(0.12, 8, 6), p.secondary, { x: sx * 0.25, y: 0.12, z: -0.25, sz: 1.3 });
    }
    s.add(geo.sphere(0.2, 10, 8), p.main, { y: 0.52, z: -0.5 });
    s.add(geo.sphere(0.15, 10, 8), p.main, { y: 0.44, z: -0.78 });
    s.add(geo.sphere(0.1, 8, 6), p.main, { y: 0.4, z: -1.0 });
    s.add(geo.cone(0.09, 0.28, 6), p.accent, { y: 0.4, z: -1.2, rx: -Math.PI / 2 });
    for (let i = 0; i < 4; i++) s.add(geo.cone(0.06, 0.16, 5), p.accent, { y: 0.92 - i * 0.07, z: 0.1 - i * 0.2 });
    return s.finish();
  },
};

/** Mulberry32 local au rendu (formes deterministes par variante). */
export const shapeRng = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/** FNV-1a 32 bits : graine stable depuis une chaine. */
const hashString = (s: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
};

export class TargetFactory {
  private readonly protos = new Map<string, TargetProto>();

  constructor(
    private readonly geo: GeometryLibrary,
    private readonly res: ResourceTracker,
  ) {}

  /**
   * Prototype (cache) de la cible `def` pour la variante `variant` (modulo def.visual.variants).
   * Les geometries sont a l'echelle FINALE (def.visual.scale appliquee) : l'entite du core porte visual.scale = def.visual.scale
   * (x1.6 boss), donc InstancedTargets multiplie par scale / def.visual.scale (voir EntityViews).
   */
  proto(def: TargetDef, variant: number): TargetProto {
    const n = Math.max(1, def.visual.variants);
    const v = ((Math.floor(variant) % n) + n) % n;
    const key = `${def.id}#${v}`;
    const cached = this.protos.get(key);
    if (cached) return cached;
    const build = THEME_BUILDERS[def.visual.theme];
    const built = build({ palette: def.visual.palette, variant: v, rand: shapeRng(hashString(key)), geo: this.geo });
    const sc = def.visual.scale;
    for (const part of built.parts) {
      if (sc !== 1) part.geometry.scale(sc, sc, sc);
      part.geometry.computeBoundingBox();
      part.geometry.computeBoundingSphere();
      this.res.track(part.geometry);
    }
    const proto: TargetProto = { key, parts: built.parts, height: built.height * sc, radius: built.radius * sc };
    if (built.canopyMid !== undefined) proto.canopyMid = built.canopyMid * sc;
    this.protos.set(key, proto);
    return proto;
  }

  /** Nombre de prototypes construits (diagnostic de memoire GPU). */
  get size(): number {
    return this.protos.size;
  }
}
